import { useCallback, useEffect, useId, useState } from 'react'
import { Link } from 'react-router-dom'
import ConfirmDialog from '../components/ConfirmDialog.jsx'
import ErrorState from '../components/ErrorState.jsx'
import InlineAlert from '../components/InlineAlert.jsx'
import { ApiError, api } from '../lib/api.js'

/**
 * `/import/review` — the rows fuzzy matching would not guess (MATCH-001).
 *
 * Each row shows what the export said against what the catalog holds, with
 * the candidates snapshotted when the question was asked. Picking one links
 * every listen the row speaks for; "none of these" is remembered and never
 * re-asked. Sorted by play count on the server: the rows that matter most to
 * the queue come first. Rows can be selected and refused in bulk, because a
 * large export asks hundreds of questions with the same answer
 * (1b-import-followup Q5).
 */

/**
 * Who a row is by, for the names a screen reader lists and for the bulk
 * dialog (Josh, 2026-09-23, QL1). Two open rows can share a title — "Clouds"
 * by two artists — but not a title, an artist and an album, which is the
 * question a row asks. `unknown artist` is the visible title's own fallback.
 */
function byLine(row) {
  const artist = row.artist || 'unknown artist'
  return row.album ? `by ${artist} (${row.album})` : `by ${artist}`
}

function rowName(row) {
  return `${row.track} ${byLine(row)}`
}

function ReviewRow({ row, selected, onSelect, onResolve, onRefuse }) {
  const titleId = useId()
  const [busy, setBusy] = useState(false)
  // This row's own sentence, beside the `busy` it already keeps. Both wrappers
  // set it: with fewer than two rows selected, *None of these* reaches the
  // same request the candidate buttons do.
  const [error, setError] = useState('')

  async function resolve(recordingId) {
    setBusy(true)
    setError('')
    try {
      await onResolve(row.id, recordingId)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that just now.')
    } finally {
      setBusy(false)
    }
  }

  async function refuse() {
    setBusy(true)
    setError('')
    try {
      await onRefuse(row.id)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that just now.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <li className="row">
      <input
        type="checkbox"
        checked={selected}
        onChange={() => onSelect(row.id)}
        aria-label={`Select ${rowName(row)}`}
      />
      <div className="row-main">
        <div className="row-title" id={titleId}>
          {row.track} <span className="footnote">— {row.artist || 'unknown artist'}</span>
        </div>
        <div className="row-sub wrap">
          {row.album ? `${row.album} · ` : ''}
          {row.plays} {row.plays === 1 ? 'play' : 'plays'} in your history
        </div>
        <div className="review-candidates">
          {/* A candidate is named by itself, so two rows offering the same one
              would read alike; the row's title is its description. */}
          {row.candidates.map((candidate) => (
            <button
              key={candidate.recording_id}
              type="button"
              className="btn btn-quiet btn-sm"
              disabled={busy}
              aria-describedby={titleId}
              onClick={() => resolve(candidate.recording_id)}
            >
              {candidate.title} — {candidate.artist}
              {candidate.album ? ` (${candidate.album})` : ''}
            </button>
          ))}
          {/* The visible words lead the name, so what is seen is what can be
              spoken to it. */}
          <button
            type="button"
            className="btn btn-quiet btn-sm"
            disabled={busy}
            aria-label={`None of these for ${rowName(row)}`}
            onClick={() => refuse()}
          >
            None of these
          </button>
        </div>
        <InlineAlert>{error}</InlineAlert>
      </div>
    </li>
  )
}

/**
 * Which rows the confirmation is about, by name rather than by count alone.
 *
 * Three titles and a remainder: a dialog that lists forty tracks is a dialog
 * nobody reads, and a bare count was the ambiguity this pass exists to remove
 * (public-beta polish audit B1). Each by its artist and album
 * (DEC-2026-09-23-008), or two "Clouds" read as the same row twice.
 */
function enumerateSelected(items, selected) {
  const titles = items
    .filter((row) => selected.has(row.id))
    .map((row) => `“${row.track}” ${byLine(row)}`)
  if (titles.length === 0) return ''
  const shown = titles.slice(0, 3).join(', ')
  const rest = titles.length - 3
  return rest > 0 ? `${shown}, and ${rest} more.` : `${shown}.`
}

export default function ImportReview() {
  const [state, setState] = useState({ loading: true, items: [] })
  const [error, setError] = useState('')
  const [selected, setSelected] = useState(() => new Set())
  // `null` when closed, otherwise `{ from }` — the row whose own button opened
  // it, or `null` for the header's. The dialog says so when the two disagree.
  const [confirmingBulk, setConfirmingBulk] = useState(null)
  const [bulkBusy, setBulkBusy] = useState(false)
  const [bulkError, setBulkError] = useState('')

  const load = useCallback(async (signal) => {
    try {
      const body = await api.matchReviews({ signal })
      setState({ loading: false, items: body.items })
    } catch (err) {
      if (err.name === 'AbortError') return
      setError(err instanceof ApiError ? err.message : 'The review list did not load.')
      setState((current) => ({ ...current, loading: false }))
    }
  }, [])

  useEffect(() => {
    document.title = 'Match review — Sonologue'
    const controller = new AbortController()
    load(controller.signal)
    return () => controller.abort()
  }, [load])

  async function resolve(reviewId, recordingId) {
    await api.resolveMatchReview(reviewId, recordingId)
    setSelected((current) => {
      const next = new Set(current)
      next.delete(reviewId)
      return next
    })
    setState((current) => ({
      ...current,
      items: current.items.filter((row) => row.id !== reviewId),
    }))
  }

  /**
   * A row's own "none of these", which is the bulk action wearing a row's
   * clothes once a selection is standing. It used to resolve the one row
   * silently while a dozen sat checked beside it, which is the opposite of
   * what somebody who has just checked a dozen rows means by it (polish pass,
   * 2026-08-05). One row selected or none is not a bulk gesture, so that
   * still resolves where it was clicked.
   *
   * Which rows resolve has not changed since that ruling and must not. What
   * changed is that the dialog now names them, and says out loud when the row
   * under the cursor is not one of them (B1).
   */
  async function refuse(reviewId) {
    if (selected.size >= 2) {
      setBulkError('')
      setConfirmingBulk({ from: reviewId })
      return
    }
    await resolve(reviewId, null)
  }

  function toggle(reviewId) {
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(reviewId)) next.delete(reviewId)
      else next.add(reviewId)
      return next
    })
  }

  async function confirmBulkNone() {
    setBulkBusy(true)
    setBulkError('')
    try {
      const ids = [...selected]
      await api.resolveMatchReviewsNone(ids)
      const gone = new Set(ids)
      setSelected(new Set())
      setConfirmingBulk(null)
      setState((current) => ({
        ...current,
        items: current.items.filter((row) => !gone.has(row.id)),
      }))
    } catch (err) {
      setBulkError(err instanceof ApiError ? err.message : 'The rows were not resolved.')
    } finally {
      setBulkBusy(false)
    }
  }

  // The row a bulk dialog was opened from that is not in the selection. Only
  // then does the dialog owe an explanation, and only then is one drawn.
  const excludedTrigger =
    confirmingBulk && confirmingBulk.from && !selected.has(confirmingBulk.from)
      ? state.items.find((row) => row.id === confirmingBulk.from)
      : null

  return (
    <>
      <div className="section-header">
        <h1 className="title-2">Match review</h1>
        {selected.size > 0 ? (
          <>
            <span className="footnote">{selected.size} selected</span>
            <button
              type="button"
              className="btn btn-quiet btn-sm"
              onClick={() => {
                setBulkError('')
                setConfirmingBulk({ from: null })
              }}
            >
              None of these
            </button>
          </>
        ) : state.items.length > 0 ? (
          <span className="footnote">{state.items.length} to review</span>
        ) : null}
      </div>

      <div className="card">
        {error ? (
          <ErrorState title="The review list did not load" message={error} onRetry={() => load()} />
        ) : state.loading ? (
          <div className="card-pad">
            <p className="footnote mb-0">Loading…</p>
          </div>
        ) : state.items.length === 0 ? (
          <div className="empty-state">
            <h2>Nothing to review</h2>
            <p>
              Everything your imports could identify has been matched. The results are waiting in
              <Link to="/queue"> your queue</Link>.
            </p>
          </div>
        ) : (
          <ul className="hairline-list">
            {state.items.map((row) => (
              <ReviewRow
                key={row.id}
                row={row}
                selected={selected.has(row.id)}
                onSelect={toggle}
                onResolve={resolve}
                onRefuse={refuse}
              />
            ))}
          </ul>
        )}
      </div>

      <ConfirmDialog
        open={confirmingBulk !== null}
        title={`None of these — ${selected.size} ${selected.size === 1 ? 'row' : 'rows'}`}
        confirmLabel="None of these"
        busy={bulkBusy}
        error={bulkError}
        onConfirm={confirmBulkNone}
        onClose={() => setConfirmingBulk(null)}
      >
        <p>
          Mark {selected.size} selected {selected.size === 1 ? 'row' : 'rows'} as none of these?{' '}
          {enumerateSelected(state.items, selected)}
        </p>
        <p>These tracks stay unmatched, and this import will not ask about them again.</p>
        <p>This cannot be undone.</p>
        {/* Commas, not parentheses: the album is already in parentheses
            (DEC-2026-09-23-008). */}
        {excludedTrigger ? (
          <p>
            The row you clicked, “{excludedTrigger.track}” {byLine(excludedTrigger)}, is not
            selected and will not be changed.
          </p>
        ) : null}
      </ConfirmDialog>
    </>
  )
}
